import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Briefcase,
  CheckCircle,
  XCircle,
  ChatCircle,
  Clock,
  Gavel,
  CalendarBlank,
  CurrencyGbp,
  PencilSimple,
  Plus,
  Scales,
  User,
  ArrowRight,
  ArrowsClockwise,
} from '@phosphor-icons/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  listInbox,
  addProposedDates,
  directApproveAllocation,
  directDeclineAllocation,
  type Allocation,
  PURPOSE_LABEL,
  FORMAT_LABEL,
} from '@/lib/allocationsApi';
import { getApiErrorMessage } from '@/lib/api';
import { nowLocalDatetime } from '@/lib/datetime';
import { onAllocationUpdate, useWebSocketStore } from '@/lib/websocket';
import { useMattersStore } from '@/lib/mattersStore';
import { RespondToBriefModal } from './RespondToBriefModal';
import { DirectApprovalModal } from './DirectApprovalModal';

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

function feeTypeLabel(t?: string | null): string {
  if (!t) return '';
  const map: Record<string, string> = {
    brief_fee: 'Brief fee', hourly: 'Hourly', fixed: 'Fixed',
    conference: 'Conference', opinion: 'Opinion', cfa: 'CFA', dba: 'DBA', pro_bono: 'Pro bono',
  };
  return map[t] ?? t;
}

export function BriefsInbox() {
  const [rows, setRows] = useState<Allocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Allocation | null>(null);
  const [directAction, setDirectAction] = useState<{ mode: 'approve' | 'decline'; allocation: Allocation } | null>(null);
  const wsConnected = useWebSocketStore((s) => s.connected);
  const fetchMatters = useMattersStore((s) => s.fetchMatters);
  // Per-row state for the "add proposed date" input
  const [proposingFor, setProposingFor] = useState<string | null>(null); // allocation id
  const [newDate, setNewDate] = useState('');
  const [proposing, setProposing] = useState(false);

  const reload = (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    listInbox()
      .then((data) => { setRows(data); setError(null); })
      .catch((err) => setError(getApiErrorMessage(err, 'Could not load inbox')))
      .finally(() => setLoading(false));
  };

  // Initial load with spinner — runs once on mount.
  useEffect(() => { reload(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling + WebSocket subscription.
  // wsConnected in deps ensures subscription re-attaches when socket connects/reconnects.
  useEffect(() => {
    const interval = setInterval(() => reload(), 300_000);
    const onVisible = () => { if (document.visibilityState === 'visible') reload(); };
    document.addEventListener('visibilitychange', onVisible);
    const unsubWs = onAllocationUpdate(() => reload());
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      unsubWs();
    };
  }, [wsConnected]); // eslint-disable-line react-hooks/exhaustive-deps

  const navigate = useNavigate();

  const handleAddDate = async (allocation: Allocation) => {
    if (!newDate || !allocation.requestedConference) return;
    setProposing(true);
    try {
      await addProposedDates(allocation.requestedConference.id, [newDate]);
      toast.success('Proposed date added — waiting for clerk to confirm');
      setProposingFor(null);
      setNewDate('');
      reload();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not add proposed date'));
    } finally {
      setProposing(false);
    }
  };

  const handleDirectAction = async (note: string) => {
    if (!directAction) return;
    const { mode, allocation } = directAction;
    if (mode === 'approve') {
      await directApproveAllocation(allocation.id, note);
      toast.success('Matter approved. It is now active in your caseload.');
    } else {
      await directDeclineAllocation(allocation.id, note);
      toast.success('Matter declined. The clerk has been notified.');
    }
    setDirectAction(null);
    reload();
    fetchMatters();
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="flex items-center gap-2 text-[15px]">
            <Briefcase className="h-4 w-4 text-brand-600 dark:text-brand-400" weight="duotone" />
            Briefs awaiting your response
            {rows.length > 0 && (
              <span className="ml-1 rounded-full bg-brand-50 px-2 py-0.5 text-xs font-semibold text-brand-700 dark:text-brand-300 ring-1 ring-brand-200">
                {rows.length}
              </span>
            )}
          </CardTitle>
          <button
            onClick={() => reload(true)}
            disabled={loading}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40"
            title="Refresh inbox"
          >
            <ArrowsClockwise className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </CardHeader>
        <CardContent className="px-0 pb-0">
          {loading ? (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">Loading…</div>
          ) : error ? (
            <div className="mx-5 my-4 rounded-md border border-danger/30 bg-danger-bg px-3 py-2 text-xs text-danger">
              {error}
            </div>
          ) : rows.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">
              No new briefs. When a clerk offers you a brief it will appear here.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {rows.map((a) => {
                const isConferenceRequested = a.status === 'conference_requested';
                const conf = a.requestedConference;
                const isProposing = proposingFor === a.id;

                const isStale = Date.now() - new Date(a.offeredAt).getTime() > 48 * 3600 * 1000;

                return (
                  <li key={a.id} className={`space-y-3 px-5 py-5 ${isStale ? 'border-l-2 border-amber-400 bg-amber-50/30 dark:bg-amber-500/10' : ''}`}>
                    {/* ── Status badge ── */}
                    {isConferenceRequested && (
                      <div className="flex items-center gap-2">
                        <Badge variant="info">
                          <ChatCircle className="mr-1 inline h-3 w-3" weight="fill" />
                          Conference requested — awaiting clerk scheduling
                        </Badge>
                      </div>
                    )}

                    {/* ── Matter headline ── */}
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="truncate text-sm font-semibold text-foreground">
                            {a.case.caseName}
                          </h3>
                          <span className="text-xs text-muted-foreground">
                            {a.case.caseNumber}
                          </span>
                          {a.case.practiceArea && (
                            <Badge variant="default">{a.case.practiceArea.name}</Badge>
                          )}
                          {a.case.caseType && (
                            <span className="text-xs text-muted-foreground">{a.case.caseType}</span>
                          )}
                        </div>

                        {/* ── Court + next hearing ── */}
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          {a.case.court && (
                            <span className="flex items-center gap-1">
                              <Gavel className="h-3 w-3" />
                              {a.case.court.shortName}
                            </span>
                          )}
                          {a.case.hearings && a.case.hearings.length > 0 && (
                            <span className="flex items-center gap-1">
                              <CalendarBlank className="h-3 w-3" />
                              {a.case.hearings.length} hearing{a.case.hearings.length > 1 ? 's' : ''}
                              {a.case.hearings[0] && (
                                <> · next {new Date(a.case.hearings[0].hearingDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</>
                              )}
                            </span>
                          )}
                          {a.case.feeEstimate && (
                            <span className="flex items-center gap-1 font-medium text-foreground">
                              <CurrencyGbp className="h-3 w-3 text-muted-foreground" />
                              £{Number(a.case.feeEstimate).toLocaleString()}
                              {a.case.feeType && <span className="font-normal text-muted-foreground"> · {feeTypeLabel(a.case.feeType)}</span>}
                            </span>
                          )}
                          <span className={`flex items-center gap-1 ${isStale ? 'font-semibold text-amber-700 dark:text-amber-300' : ''}`}>
                            <Clock className="h-3 w-3" />
                            offered {timeAgo(a.offeredAt)}
                            {isStale && <span className="ml-1 rounded bg-amber-100 dark:bg-amber-500/15 px-1 py-0.5 text-[9px] font-bold uppercase text-amber-700 dark:text-amber-300">Awaiting response</span>}
                          </span>
                        </div>

                        {/* ── Parties ── */}
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                          {(a.case.layClient) && (
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <User className="h-3 w-3" />
                              <span className="font-medium text-foreground">
                                {a.case.layClient.company ?? `${a.case.layClient.firstName ?? ''} ${a.case.layClient.lastName ?? ''}`.trim()}
                              </span>
                              <span className="text-[10px]">(lay client)</span>
                            </span>
                          )}
                          {a.case.instructingSolicitor && (
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <Scales className="h-3 w-3" />
                              <span className="font-medium text-foreground">
                                {a.case.instructingSolicitor.firmName ??
                                  `${a.case.instructingSolicitor.firstName ?? ''} ${a.case.instructingSolicitor.lastName ?? ''}`.trim()}
                              </span>
                              <span className="text-[10px]">(solicitor)</span>
                            </span>
                          )}
                          {a.case.opponentParty && (
                            <span className="text-muted-foreground">
                              <span className="italic">v </span>
                              <span className="font-medium text-foreground">{a.case.opponentParty}</span>
                              {a.case.opponentCounsel && (
                                <span className="text-[10px]"> ({a.case.opponentCounsel})</span>
                              )}
                            </span>
                          )}
                        </div>

                        {/* ── Matter description ── */}
                        {a.case.description && (
                          <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-muted-foreground">
                            {a.case.description}
                          </p>
                        )}

                        {/* ── Clerk note ── */}
                        {a.clerkNote && (
                          <div className="mt-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs italic text-muted-foreground">
                            "{a.clerkNote}" —{' '}
                            <span className="not-italic font-medium">
                              {a.offeredBy.firstName} {a.offeredBy.lastName}
                            </span>
                          </div>
                        )}

                        {/* ── Conference details (if requested) ── */}
                        {isConferenceRequested && conf && (
                          <div className="mt-3 rounded-md border border-brand-200 bg-brand-50/40 p-3 text-xs">
                            <div className="mb-1.5 font-semibold text-brand-800 dark:text-brand-200">Your conference request</div>
                            <div className="text-muted-foreground">
                              {PURPOSE_LABEL[conf.purpose]} · {FORMAT_LABEL[conf.format]}
                              {conf.layClientAttending && ' · Lay client attending'}
                              {' · '}{conf.durationMinutes}min
                            </div>
                            {conf.notes && (
                              <div className="mt-1 italic text-muted-foreground">"{conf.notes}"</div>
                            )}
                            {conf.proposedDates && conf.proposedDates.length > 0 && (
                              <div className="mt-2">
                                <span className="font-medium text-foreground">Proposed slots:</span>{' '}
                                {conf.proposedDates.map((d: string) =>
                                  new Date(d).toLocaleString('en-GB', {
                                    weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                                  }),
                                ).join(' · ')}
                              </div>
                            )}
                            {conf.scheduledAt ? (
                              <div className="mt-2 flex items-center gap-1.5 text-success font-medium">
                                <CheckCircle className="h-3.5 w-3.5" weight="fill" />
                                Confirmed for {new Date(conf.scheduledAt).toLocaleString('en-GB', {
                                  weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit',
                                })}
                              </div>
                            ) : (
                              /* Editable until clerk confirms */
                              <div className="mt-2">
                                {!isProposing ? (
                                  <button
                                    onClick={() => { setProposingFor(a.id); setNewDate(''); }}
                                    className="flex items-center gap-1 text-xs font-medium text-brand-700 dark:text-brand-300 hover:underline"
                                  >
                                    <PencilSimple className="h-3 w-3" />
                                    Add another proposed slot
                                  </button>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="datetime-local"
                                      value={newDate}
                                      onChange={(e) => setNewDate(e.target.value)}
                                      min={nowLocalDatetime()}
                                      step={60}
                                      className="h-8 rounded-md border border-border bg-card px-2 text-xs focus:border-brand-600 focus:outline-none"
                                    />
                                    <Button
                                      size="xs"
                                      variant="primary"
                                      leftIcon={<Plus className="h-3 w-3" />}
                                      disabled={!newDate || proposing}
                                      onClick={() => handleAddDate(a)}
                                    >
                                      {proposing ? 'Adding…' : 'Add slot'}
                                    </Button>
                                    <Button size="xs" variant="ghost" onClick={() => setProposingFor(null)}>
                                      Cancel
                                    </Button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* ── Action buttons ── */}
                      {isConferenceRequested ? (
                        /* Conference is pending/scheduled — barrister can decide now
                           (e.g. clerk called on the phone and explained everything) */
                        <div className="flex flex-col gap-1.5">
                          <Button
                            size="sm"
                            variant="primary"
                            leftIcon={<CheckCircle className="h-3.5 w-3.5" weight="fill" />}
                            onClick={() => setDirectAction({ mode: 'approve', allocation: a })}
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            leftIcon={<XCircle className="h-3.5 w-3.5" />}
                            onClick={() => setDirectAction({ mode: 'decline', allocation: a })}
                          >
                            Decline
                          </Button>
                        </div>
                      ) : (
                        /* Brief is pending — standard three-option response */
                        <div className="flex flex-col gap-1.5">
                          <Button
                            size="sm"
                            variant="primary"
                            leftIcon={<CheckCircle className="h-3.5 w-3.5" weight="fill" />}
                            onClick={() => setSelected({ ...a, _action: 'accept' } as Allocation & { _action: string })}
                          >
                            Accept
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            leftIcon={<ChatCircle className="h-3.5 w-3.5" />}
                            onClick={() => setSelected({ ...a, _action: 'request_con' } as Allocation & { _action: string })}
                          >
                            Request con
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            leftIcon={<XCircle className="h-3.5 w-3.5" />}
                            onClick={() => setSelected({ ...a, _action: 'decline' } as Allocation & { _action: string })}
                          >
                            Decline
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* ── Read full brief link ── */}
                    <button
                      onClick={() => navigate(`/barrister/matters/${a.case.id}`)}
                      className="flex items-center gap-1 text-xs font-medium text-brand-700 dark:text-brand-300 hover:underline"
                    >
                      Read full matter detail <ArrowRight className="h-3 w-3" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {selected && (
        <RespondToBriefModal
          allocation={selected}
          initialAction={(selected as Allocation & { _action?: 'accept' | 'decline' | 'request_con' })._action ?? 'accept'}
          onClose={() => setSelected(null)}
          onSuccess={() => {
            setSelected(null);
            reload();
          }}
        />
      )}

      {directAction && (
        <DirectApprovalModal
          mode={directAction.mode}
          matterName={directAction.allocation.case.caseName}
          onConfirm={handleDirectAction}
          onClose={() => setDirectAction(null)}
        />
      )}
    </>
  );
}
